import type { ProjectDay } from "../../../api/schemas";
import { resolveProjectDays } from "../../../domain/projectAttribution";
import type { AttributedBlock, AttributionInput } from "../../../domain/projectAttribution";
import { parseInstant } from "../../../domain/time";
import type { EpochSeconds } from "../../../domain/time";
import type { WorkBlock, WorkDay } from "../../../domain/workday";
import type { LocalAttribution } from "../../../offline/model";

/** The segments of a day for the day overview (T-125). */
export interface DaySegments {
  readonly blocks: readonly AttributedBlock[];
  readonly workSeconds: number;
  readonly unattributedSeconds: number;
}

/**
 * Converts local attributions for the resolution; rejected ones never count.
 *
 * @param attributions - Attributions on this device.
 * @returns The inputs.
 */
export function toAttributionInputs(attributions: readonly LocalAttribution[]): AttributionInput[] {
  const inputs = attributions
    .filter((item) => item.syncStatus !== "rejected")
    .map((item) => ({
      id: item.id,
      kind: item.kind,
      projectId: item.projectId,
      occurredAt: item.occurredAt,
      rangeStart: item.rangeStart,
      rangeEnd: item.rangeEnd,
      supersedesAttributionId: item.supersedesAttributionId,
      recordedAtMs: item.createdAtMs,
      activityId: item.activityId ?? null,
    }));

  return inputs;
}

/**
 * Summarizes resolved blocks.
 *
 * @param blocks - Blocks with projects.
 * @returns The segments with the working and unattributed time.
 */
function summarize(blocks: readonly AttributedBlock[]): DaySegments {
  const work = blocks.filter((block) => block.kind === "Work");
  const workSeconds = work.reduce((sum, block) => sum + (block.end - block.start), 0);
  const unattributedSeconds = work
    .filter((block) => block.projectId === null)
    .reduce((sum, block) => sum + (block.end - block.start), 0);
  const segments: DaySegments = { blocks, workSeconds, unattributedSeconds };

  return segments;
}

/**
 * Builds the segments of a day the server already resolved (open days of the past) and applies the
 * ranges recorded on this device that the server has not seen yet.
 *
 * @param serverDay - The project view of the day from the server.
 * @param local - Attributions on this device.
 * @returns The segments.
 */
export function segmentsFromServerDay(
  serverDay: ProjectDay,
  local: readonly LocalAttribution[],
): DaySegments {
  const blocks: WorkBlock[] = serverDay.blocks.map((block) => ({
    kind: block.kind,
    start: parseInstant(block.start),
    end: parseInstant(block.end),
    isOpen: block.isOpen,
    projectId: block.projectId,
    startEventId: "",
    endEventId: null,
    // The project view has no break kinds; project segments never use them (PINV-P02).
    breakQualification: "Unclassified",
  }));
  const first = blocks[0]?.start ?? 0;
  const last = blocks.at(-1)?.end ?? first;
  const day: WorkDay = {
    date: serverDay.date,
    timeZoneId: "UTC",
    shifts: [{ start: first, end: last }],
    blocks,
    workSeconds: serverDay.workSeconds,
    breakSeconds: 0,
    isClosed: serverDay.isClosed,
    firstStart: first,
    lastEnd: last,
    closedAt: serverDay.isClosed ? last : null,
  };
  // The server's projects act as the live projects; only local ranges can override them. Parts with
  // an activity (T-184) keep it as a range recorded before every local one.
  const withActivity: AttributionInput[] = serverDay.blocks
    .filter((block) => block.kind === "Work" && (block.activityId ?? null) !== null)
    .map((block, index) => ({
      id: `server-activity-${String(index)}`,
      kind: "Range",
      projectId: block.projectId,
      occurredAt: null,
      rangeStart: parseInstant(block.start),
      rangeEnd: parseInstant(block.end),
      supersedesAttributionId: null,
      recordedAtMs: 0,
      activityId: block.activityId ?? null,
    }));
  const ranges = toAttributionInputs(
    local.filter((item) => item.kind === "Range" && item.syncStatus !== "synced"),
  );
  const resolved = resolveProjectDays([day], [...withActivity, ...ranges])[0];
  const segments = summarize(resolved?.blocks ?? []);

  return segments;
}

/**
 * Builds the segments of a day of the local timeline (the day just clocked out): the projects the
 * server resolved for closed blocks, overridden by the attributions on this device. The open block
 * of the snapshot keeps its project up to the end of its local block or the next switch on this
 * device: a shift synchronized before is rebuilt from the server's working days, whose clock
 * events carry no project, so the project of the clock-in would otherwise read „Kein Projekt“ until
 * the next synchronization (F-24).
 *
 * @param localDay - The working day from snapshot and local events.
 * @param serverDay - The project view of that day from the snapshot, if any.
 * @param local - Attributions on this device.
 * @returns The segments.
 */
export function segmentsFromLocalDay(
  localDay: WorkDay,
  serverDay: ProjectDay | null,
  local: readonly LocalAttribution[],
): DaySegments {
  const fromServer: AttributionInput[] = (serverDay?.blocks ?? [])
    .filter((block) => block.kind === "Work" && !block.isOpen && block.projectId !== null)
    .map((block, index) => ({
      id: `server-${String(index)}`,
      kind: "Range",
      projectId: block.projectId,
      occurredAt: null,
      rangeStart: parseInstant(block.start),
      rangeEnd: parseInstant(block.end),
      supersedesAttributionId: null,
      recordedAtMs: 0,
      activityId: block.activityId ?? null,
    }));
  const localInputs = toAttributionInputs(local);
  const switchTimes = localInputs
    .filter((item) => item.kind === "Switch" && item.occurredAt !== null)
    .map((item) => item.occurredAt ?? 0);
  const fromOpenBlock: AttributionInput[] = (serverDay?.blocks ?? [])
    .filter((block) => block.kind === "Work" && block.isOpen && block.projectId !== null)
    .flatMap((block, index) => {
      const start = parseInstant(block.start);
      const localBlock = localDay.blocks.find(
        (candidate) =>
          candidate.kind === "Work" && candidate.start <= start && start < candidate.end,
      );

      if (localBlock === undefined) {
        return [];
      }

      const end = Math.min(localBlock.end, ...switchTimes.filter((time) => time > start));
      const input: AttributionInput = {
        id: `server-open-${String(index)}`,
        kind: "Range",
        projectId: block.projectId,
        occurredAt: null,
        rangeStart: start,
        rangeEnd: end,
        supersedesAttributionId: null,
        recordedAtMs: 0,
        activityId: block.activityId ?? null,
      };

      return [input];
    });
  const resolved = resolveProjectDays(
    [localDay],
    [...fromServer, ...fromOpenBlock, ...localInputs],
  )[0];
  const segments = summarize(resolved?.blocks ?? []);

  return segments;
}

/**
 * Splits the work blocks at the chosen instants, so that parts of one block can be attributed to
 * different projects ("aufteilen").
 *
 * @param blocks - Blocks with projects.
 * @param cuts - Instants to split at.
 * @returns The blocks, split where a cut lies strictly inside a work block.
 */
export function splitBlocks(
  blocks: readonly AttributedBlock[],
  cuts: readonly EpochSeconds[],
): AttributedBlock[] {
  const split = blocks.flatMap((block) => {
    const inside = [...new Set(cuts)]
      .filter((cut) => block.kind === "Work" && cut > block.start && cut < block.end)
      .sort((left, right) => left - right);
    const bounds = [block.start, ...inside, block.end];
    const parts = bounds.slice(0, -1).map((start, index) => ({
      ...block,
      start,
      end: bounds[index + 1] ?? block.end,
      isOpen: block.isOpen && index === bounds.length - 2,
    }));

    return parts;
  });

  return split;
}
